import React, { useState } from "react";
import { DollarSign, Calendar, FileText, X, Loader2, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function RecordPaymentModal({ leadId, dealNumber, onClose, onSuccess, token }) {
  const [amount, setAmount] = useState("");
  const [paymentType, setPaymentType] = useState("interest");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) <= 0) { setError("Enter a valid amount"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/servicing/record-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          lead_id: leadId,
          amount: parseFloat(amount),
          payment_type: paymentType,
          payment_date: paymentDate,
          notes: notes || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Failed to record payment"); setLoading(false); return; }
      if (data.status === "duplicate") { setError("This payment was already recorded (duplicate detected)"); setLoading(false); return; }
      setSuccess(data);
      setTimeout(() => { onSuccess && onSuccess(data); }, 1200);
    } catch (err) {
      setError("Network error — please try again");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-testid="payment-success-overlay">
        <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full text-center shadow-2xl">
          <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
          <h3 className="text-lg font-bold text-[#0A0A0A]">Payment Recorded</h3>
          <p className="text-sm text-[#666] mt-2">${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })} ({paymentType}) on {paymentDate}</p>
          <p className="text-xs text-[#999] mt-1">ID: {success.payment_id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-0 sm:p-4" data-testid="record-payment-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e8e0d0]">
          <div>
            <h2 className="text-base font-bold text-[#0A0A0A]" data-testid="payment-modal-title">Record Payment</h2>
            <p className="text-xs text-[#888] mt-0.5">{dealNumber || "Deal"}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#f5f0e8] transition-colors" data-testid="payment-modal-close">
            <X className="h-4 w-4 text-[#666]" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-xs font-semibold text-[#333] mb-1.5">Payment Amount *</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-[#d4cfc5] bg-[#faf7f2] text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                required
                data-testid="payment-amount-input"
              />
            </div>
          </div>

          {/* Payment Type */}
          <div>
            <label className="block text-xs font-semibold text-[#333] mb-1.5">Payment Type *</label>
            <div className="grid grid-cols-3 gap-2" data-testid="payment-type-selector">
              {["interest", "principal", "mixed"].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setPaymentType(type)}
                  className={`px-3 py-2.5 rounded-xl text-xs font-semibold capitalize transition-all border ${
                    paymentType === type
                      ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                      : "bg-[#faf7f2] text-[#666] border-[#d4cfc5] hover:border-emerald-400"
                  }`}
                  data-testid={`payment-type-${type}`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-xs font-semibold text-[#333] mb-1.5">Payment Date *</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#999]" />
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-[#d4cfc5] bg-[#faf7f2] text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                required
                data-testid="payment-date-input"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-semibold text-[#333] mb-1.5">Notes (optional)</label>
            <div className="relative">
              <FileText className="absolute left-3 top-3 h-4 w-4 text-[#999]" />
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Payment reference, wire confirmation, etc."
                rows={2}
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-[#d4cfc5] bg-[#faf7f2] text-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 resize-none"
                data-testid="payment-notes-input"
              />
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-xs text-red-700" data-testid="payment-error">
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !amount}
            className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            data-testid="record-payment-submit"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Processing...</> : "Record Payment"}
          </button>
        </form>
      </div>
    </div>
  );
}
